<template>
  <div class="ingredient-card">
    <!-- head: name + status chip -->
    <div class="d-flex justify-content-between align-items-start gap-2">
      <div class="flex-grow-1">
        <div class="fw-semibold text-body" style="font-size: 14px;">
          {{ input.name }}
        </div>
        <div
          class="small mt-1"
          v-if="!['completed', 'cancelled'].includes($parent.data.status)"
        >
          <span class="text-secondary">{{ $t('In stock') }}:</span>
          <span :class="stockShort ? 'text-danger fw-semibold' : 'text-success'">
            {{ formatDigit(input.remaining_quantity) }} {{ $t(input.unit) }}
          </span>
          <i
            v-if="stockShort"
            class="fa-solid fa-triangle-exclamation text-danger ms-1"
            :title="$t('Stock is lower than the planned quantity.')"
          />
        </div>
      </div>
      <span
        class="badge rounded-pill align-self-center"
        :class="statusChip.class"
        style="font-size: 11px; font-weight: 500;"
      >
        <i :class="statusChip.icon" class="me-1" />{{ statusChip.text }}
      </span>
    </div>

    <!-- draft: planned only -->
    <div
      v-if="$parent.data.status === 'draft'"
      class="mt-2"
      style="font-size: 13px;"
    >
      <span class="text-secondary">{{ $t('Planned Quantity') }}:</span>
      <b class="ms-1">{{ formatDigit(input.planned_quantity) }}</b> {{ $t(input.unit) }}
    </div>

    <!-- recorded states: progress bar -->
    <div
      v-else
      class="mt-2"
    >
      <div class="progress" style="height: 6px; border-radius: 4px;">
        <div
          class="progress-bar"
          :class="barClass"
          role="progressbar"
          :style="{ width: progressPct + '%' }"
        ></div>
      </div>
      <div
        class="d-flex justify-content-between align-items-center mt-1"
        style="font-size: 12px;"
      >
        <span class="text-secondary" v-if="!isUnplanned">
          {{ $t('Recorded') }}
          <b class="text-body">{{ formatDigit(input.actual_quantity) }}</b>
          / {{ formatDigit(input.planned_quantity) }} {{ $t(input.unit) }}
        </span>
        <span class="text-secondary" v-else>
          {{ $t('Recorded') }}
          <b class="text-body">{{ formatDigit(input.actual_quantity) }}</b>
          {{ $t(input.unit) }}
        </span>
        <span v-if="!isUnplanned" :class="remainingInfo.class">{{ remainingInfo.text }}</span>
      </div>
      <div
        class="d-flex justify-content-between mt-1"
        style="font-size: 12px;"
        v-if="actual > 0"
      >
        <span class="text-secondary">{{ $t('Cost used') }}</span>
        <span class="fw-semibold">{{ formatDigit(input.actual_total_cost) }} {{ $t('THB') }}</span>
      </div>
    </div>

    <!-- actions -->
    <div
      v-if="['pending', 'progressing'].includes($parent.data.status)"
      class="mt-2 d-flex gap-2 flex-wrap"
    >
      <button
        v-if="canAutoFill"
        type="button"
        class="btn btn-sm btn-theme flex-fill"
        style="font-size: 11px;"
        @click="$parent.handleAutoFill(input)"
      >
        <i class="fa-solid fa-wand-magic-sparkles me-1" />
        {{ $t('Auto-fill') }}
      </button>
      <button
        type="button"
        class="btn btn-sm flex-fill"
        :class="canRecord ? 'btn-outline-theme' : 'btn-light border text-secondary'"
        style="font-size: 11px;"
        :disabled="!canRecord"
        @click="() => {
          if (!canRecord) return;
          $parent.modalRecordLotForm.open = true;
          $parent.modalRecordLotForm.name = input.name;
          $parent.modalRecordLotForm.inventoryId = input.inventory_id;
          $parent.modalRecordLotForm.unit = input.unit;
          $parent.modalRecordLotForm.id = input.id;
        }"
      >
        <i class="fa-solid fa-pen-to-square me-1" />
        {{ $t('Record Lots') }}
      </button>
      <button
        v-if="$parent.data.status === 'progressing' && parseFloat(input.actual_quantity) > 0"
        type="button"
        class="btn btn-sm btn-outline-danger flex-fill"
        style="font-size: 11px;"
        @click="() => {
          $parent.modalReverseRecordForm.open = true;
          $parent.modalReverseRecordForm.name = input.name;
          $parent.modalReverseRecordForm.unit = input.unit;
          $parent.modalReverseRecordForm.id = input.id;
        }"
      >
        <i class="fa-solid fa-rotate-left me-1" />
        {{ $t('Reverse') }}
      </button>
      <button
        v-if="$parent.data.status === 'progressing' && actual <= 0.000001"
        type="button"
        class="btn btn-sm btn-outline-danger"
        style="font-size: 11px;"
        :title="$t('Remove Ingredient')"
        @click="$parent.handleDeleteInput(input)"
      >
        <i class="fa-solid fa-trash-can" />
      </button>
    </div>
  </div>
</template>

<script lang="js">
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-view-input-form";
import numeral from 'numeral';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.input)
  },
  props: {
    input: {
      type: Object,
      default: []
    },
    index: {
      type: Number,
      required: true
    }
  },
  methods: {
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
  },
  computed: {
    planned() {
      return parseFloat(this.input.planned_quantity) || 0;
    },
    actual() {
      return parseFloat(this.input.actual_quantity) || 0;
    },
    progressPct() {
      if (this.planned <= 0) return this.actual > 0 ? 100 : 0;
      return Math.min(100, (this.actual / this.planned) * 100);
    },
    isUnplanned() {
      return !!this.input.is_unplanned;
    },
    isOver() {
      return !this.isUnplanned && this.actual > this.planned + 0.000001;
    },
    isComplete() {
      return this.planned > 0 && this.actual >= this.planned - 0.000001;
    },
    canRecord() {
      return this.$parent.data.status === 'progressing';
    },
    canAutoFill() {
      return this.$parent.data.status === 'progressing' && !this.isUnplanned && this.actual < this.planned - 0.000001;
    },
    stockShort() {
      return parseFloat(this.input.remaining_quantity) < this.planned;
    },
    barClass() {
      if (this.isUnplanned) return this.actual > 0 ? 'bg-info' : 'bg-secondary opacity-25';
      if (this.isOver) return 'bg-danger';
      if (this.isComplete) return 'bg-success';
      if (this.actual > 0) return 'bg-warning';
      return 'bg-secondary opacity-25';
    },
    statusChip() {
      if (this.isUnplanned) {
        return { text: this.$t('Unplanned'), class: 'text-bg-info', icon: 'fa-solid fa-plus' };
      }
      if (this.$parent.data.status === 'draft') {
        return { text: this.$t('Planned'), class: 'text-bg-light border text-secondary', icon: 'fa-regular fa-clipboard' };
      }
      if (this.isOver) {
        return { text: this.$t('Over plan'), class: 'text-bg-danger', icon: 'fa-solid fa-triangle-exclamation' };
      }
      if (this.isComplete) {
        return { text: this.$t('Complete'), class: 'text-bg-success', icon: 'fa-solid fa-check' };
      }
      if (this.actual > 0) {
        return { text: this.$t('Partial'), class: 'text-bg-warning', icon: 'fa-solid fa-hourglass-half' };
      }
      return { text: this.$t('Not recorded'), class: 'text-bg-light border text-secondary', icon: 'fa-regular fa-circle' };
    },
    remainingInfo() {
      if (this.isOver) {
        return { text: '+' + this.formatDigit(this.actual - this.planned) + ' ' + this.$t('over plan'), class: 'text-danger fw-semibold' };
      }
      if (this.isComplete) {
        return { text: this.$t('Complete'), class: 'text-success fw-semibold' };
      }
      return { text: this.$t('Need') + ' ' + this.formatDigit(this.planned - this.actual), class: 'text-warning fw-semibold' };
    }
  }
}
</script>

<style scoped>
.ingredient-card {
  border: 1px solid #eef0f2;
  border-radius: 0.6rem;
  background-color: #fff;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.5rem;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.ingredient-card:hover {
  border-color: #e0e3e7;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.ingredient-card:last-child {
  margin-bottom: 0;
}
</style>
